import { useState } from 'react';
import { createPortal } from 'react-dom';
import { LayoutList, X, CheckCircle2, Trophy, Users, Shield, AlertCircle, LayoutGrid, Zap } from 'lucide-react';
import { doc, collection, setDoc } from 'firebase/firestore';
import { handleFirestoreError } from '../../lib/errorHandlers';
import { Team, Tournament, Bracket, BracketMatch, Player } from '../../types';
import { logActivity } from '../../services/activityLogger';
import { auth } from '../../lib/firebase';

interface AutoTournamentWizardProps {
  allTeams: Team[];
  db: any;
  getCollectionPath: (col: string) => string;
  onCancel: () => void;
  organizerId: string;
  players?: Player[];
  userRole?: string;
}

export function AutoTournamentWizard({ allTeams, db, getCollectionPath, onCancel, organizerId, players = [], userRole }: AutoTournamentWizardProps) {
  const [step, setStep] = useState(1);
  const [compType, setCompType] = useState<'liga' | 'torneo_eliminacion'>('liga');
  const [name, setName] = useState('');
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [category, setCategory] = useState('1era');
  const [gender, setGender] = useState<'Masculino' | 'Femenino' | 'Mixto' | 'Sin Restricción'>('Sin Restricción');
  const [isMaster, setIsMaster] = useState(false);

  // Liga options
  const [numZones, setNumZones] = useState(1);
  const [doubleRound, setDoubleRound] = useState(false);
  const [hasPlayoffs, setHasPlayoffs] = useState(true);
  const [qualifiersPerZone, setQualifiersPerZone] = useState(2);

  const [isLoading, setIsLoading] = useState(false);

  const toggleTeam = (id: string) => {
    if (selectedTeams.includes(id)) setSelectedTeams(selectedTeams.filter((t) => t !== id));
    else setSelectedTeams([...selectedTeams, id]);
  };

  const selectAll = () => {
    if (selectedTeams.length === allTeams.length) setSelectedTeams([]);
    else setSelectedTeams(allTeams.map((t) => t.id));
  };

  const generateRoundRobin = (teamsArr: (Team | null)[], tName: string, zName: string, tId: string, zIdx: number, doubleRnd: boolean) => {
    let t = [...teamsArr];
    if (t.length % 2 !== 0) t.push(null);
    const n = t.length;
    const rounds = n - 1;
    const matches = [];

    for (let r = 0; r < rounds; r++) {
      for (let i = 0; i < n / 2; i++) {
        const t1 = t[i];
        const t2 = t[n - 1 - i];
        if (t1 !== null || t2 !== null) {
          const mId = doc(collection(db, getCollectionPath('matches'))).id;
          let team1Id = '';
          let team2Id = '';
          let participantIds: string[] = [];
          
          if (t1 !== null && t2 !== null) {
            team1Id = t1.id;
            team2Id = t2.id;
            if (t1.player1Id) participantIds.push(t1.player1Id);
            if (t1.player2Id) participantIds.push(t1.player2Id);
            if (t1.player3Id) participantIds.push(t1.player3Id);
            if (t2.player1Id) participantIds.push(t2.player1Id);
            if (t2.player2Id) participantIds.push(t2.player2Id);
            if (t2.player3Id) participantIds.push(t2.player3Id);
          } else if (t1 !== null) {
            team1Id = t1.id;
            team2Id = 'BYE';
            if (t1.player1Id) participantIds.push(t1.player1Id);
            if (t1.player2Id) participantIds.push(t1.player2Id);
            if (t1.player3Id) participantIds.push(t1.player3Id);
          } else if (t2 !== null) {
            team1Id = t2.id;
            team2Id = 'BYE';
            if (t2.player1Id) participantIds.push(t2.player1Id);
            if (t2.player2Id) participantIds.push(t2.player2Id);
            if (t2.player3Id) participantIds.push(t2.player3Id);
          }

          matches.push({
            id: mId,
            round: `Fecha ${r + 1}`,
            roundNumber: r + 1,
            tournamentId: tId,
            tournamentName: tName,
            zoneName: zName,
            zoneIndex: zIdx,
            team1Id,
            team2Id,
            participantIds,
            organizerId,
            status: 'pending' as const,
            s1: '',
            s2: '',
            s3: '',
            winnerId: null,
            createdAt: Date.now() + matches.length,
          });
        }
      }
      t.splice(1, 0, t.pop()!);
    }

    if (doubleRnd) {
      const idaMatches = [...matches];
      const roundsOffset = rounds;
      idaMatches.forEach((m) => {
        const mId = doc(collection(db, getCollectionPath('matches'))).id;
        matches.push({
          ...m,
          id: mId,
          round: `Fecha ${m.roundNumber! + roundsOffset} (vuelta)`,
          roundNumber: m.roundNumber! + roundsOffset,
          team1Id: m.team2Id,
          team2Id: m.team1Id,
          participantIds: m.participantIds,
          createdAt: Date.now() + matches.length,
        });
      });
    }
    return matches;
  };

  const generateBrackets = (teamsArr: Team[]) => {
    const n = teamsArr.length;
    if (n < 2) return [];

    // Find next power of 2
    let pow2 = 1;
    while (pow2 < n) pow2 *= 2;

    const brackets: Bracket[] = [];
    let currentRoundTeams: (Team | { placeholder: string; isBye?: boolean })[] = [...teamsArr];

    // Fill with PASE DIRECTO if needed (simple version)
    const byesNeeded = pow2 - n;
    for (let i = 0; i < byesNeeded; i++) {
      currentRoundTeams.push({ placeholder: 'PASE DIRECTO', isBye: true });
    }

    // Shuffle teams for the bracket
    for (let i = currentRoundTeams.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [currentRoundTeams[i], currentRoundTeams[j]] = [currentRoundTeams[j], currentRoundTeams[i]];
    }

    const getRoundName = (teamsCount: number) => {
      if (teamsCount === 2) return 'Final';
      if (teamsCount === 4) return 'Semifinal';
      if (teamsCount === 8) return 'Cuartos de final';
      if (teamsCount === 16) return 'Octavos de final';
      return `Ronda de ${teamsCount}`;
    };

    let teamsAtThisRound = pow2;
    while (teamsAtThisRound >= 2) {
      const roundName = getRoundName(teamsAtThisRound);
      const roundMatches: BracketMatch[] = [];
      const matchesInRound = teamsAtThisRound / 2;

      for (let i = 0; i < matchesInRound; i++) {
        // En la primera ronda (cuando teamsAtThisRound === pow2) usamos los equipos mezclados
        // En las siguientes rondas dejamos los campos de IDs vacíos para que avancen los ganadores
        const isFirstRound = teamsAtThisRound === pow2;
        let t1Id = '';
        let t2Id = '';
        let t1Name = 'A DEFINIR';
        let t2Name = 'A DEFINIR';

        if (isFirstRound) {
          const teamA = currentRoundTeams[i * 2];
          const teamB = currentRoundTeams[i * 2 + 1];

          if ('id' in teamA) {
            t1Id = teamA.id;
            t1Name = teamA.name;
          } else {
            t1Id = 'BYE';
            t1Name = 'PASE DIRECTO';
          }

          if ('id' in teamB) {
            t2Id = teamB.id;
            t2Name = teamB.name;
          } else {
            t2Id = 'BYE';
            t2Name = 'PASE DIRECTO';
          }
        }

        roundMatches.push({
          team1Id: t1Id,
          team2Id: t2Id,
          t1: t1Name,
          t2: t2Name,
          s1: '', s2: '', s3: '', score: '',
          winnerId: ''
        });
      }

      brackets.push({ round: roundName, matches: roundMatches });
      teamsAtThisRound /= 2;
    }

    return brackets;
  };

  const handleGenerate = async () => {
    if (!name || selectedTeams.length < 2) return;
    setIsLoading(true);

    const activeSelectedTeams = allTeams.filter((t) => selectedTeams.includes(t.id));
    let shuffled = [...activeSelectedTeams];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }

    try {
      const newTournamentRef = doc(collection(db, getCollectionPath('tournaments')));
      const tId = newTournamentRef.id;

      if (compType === 'liga') {
        const zones = Array.from({ length: numZones }).map((_, i) => ({
          name: `Zona ${String.fromCharCode(65 + i)}`,
          teams: [] as any[],
        }));

        shuffled.forEach((team, idx) => {
          const zIdx = idx % numZones;
          zones[zIdx].teams.push({ teamId: team.id, name: team.name, pts: 0, pj: 0, pg: 0, sf: 0, sc: 0 });
        });

        await setDoc(newTournamentRef, {
          id: tId,
          name,
          category,
          gender,
          status: hasPlayoffs ? 'Fase de Grupos' : 'En Juego',
          isArchived: false,
          championId: '',
          zones,
          brackets: [],
          rulesText: '',
          rulesPdfUrl: '',
          type: 'liga',
          hasPlayoffs,
          qualifiersPerZone: hasPlayoffs ? qualifiersPerZone : 0,
          organizerId,
          createdAt: Date.now(),
          isMaster: isMaster
        });

        let allMatches: any[] = [];
        zones.forEach((z, zIdx) => {
          const zoneTeams = shuffled.filter((_, idx) => idx % numZones === zIdx);
          const zoneMatches = generateRoundRobin(zoneTeams, name, z.name, tId, zIdx, doubleRound);
          allMatches = [...allMatches, ...zoneMatches];
        });

        for (const matchData of allMatches) {
          await setDoc(doc(db, getCollectionPath('matches'), matchData.id), matchData);
        }
      } else {
        // Torneo Eliminación
        const generatedBrackets = generateBrackets(shuffled);
        await setDoc(newTournamentRef, {
          id: tId,
          name,
          category,
          gender,
          status: 'Eliminatorias',
          isArchived: false,
          championId: '',
          zones: [],
          brackets: generatedBrackets,
          rulesText: '',
          rulesPdfUrl: '',
          type: 'torneo_eliminacion',
          organizerId,
          createdAt: Date.now(),
          isMaster: isMaster
        });
      }

      // Registro de Actividad
      const currentUser = auth.currentUser;
      const currentUserProfile = players.find(p => p.id === currentUser?.uid);
      const activeUserName = currentUserProfile?.name || currentUser?.displayName || currentUser?.email || 'Administrador';

      await logActivity(
        db,
        getCollectionPath,
        organizerId,
        activeUserName,
        userRole || 'gestor_torneos',
        `Creación de torneo automático: ${name} (${category} - ${compType === 'liga' ? 'Liga' : 'Eliminación Directa'})`
      );

      setIsLoading(false);
      onCancel();

      // Notify parent to open and select this new tournament
      window.dispatchEvent(new CustomEvent('openAdminTournament', { detail: tId }));
      const targetTab = compType === 'liga' ? (hasPlayoffs ? 'activos' : 'activos') : 'activos';
      // Actually, all auto-created tournaments are immediately active or grouped
      window.dispatchEvent(new CustomEvent('changeAdminTab', { detail: { tab: 'activos', tournamentId: tId } }));
    } catch (error) {
      setIsLoading(false);
      handleFirestoreError(error, 'write', 'tournaments/matches');
    }
  };

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 bg-black/30 backdrop-blur-sm overflow-y-auto animate-in fade-in duration-300" onClick={onCancel}>
      <div 
        className="bg-[#1a1c23]/95 border border-white/10 p-6 md:p-8 rounded-[2rem] relative shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-500 max-w-xl w-full mx-auto my-auto"
        onClick={e => e.stopPropagation()}
      >
        {isLoading && (
        <div className="absolute inset-0 bg-black/80 z-50 flex flex-col items-center justify-center rounded-[2rem] backdrop-blur-md">
          <Zap className="w-16 h-16 theme-primary-text animate-pulse mb-4" />
          <span className="text-xl font-black theme-primary-text uppercase tracking-widest">Generando competición...</span>
        </div>
      )}

      {/* Header */}
      <div className="relative flex justify-center items-start mb-6 pt-2">
        <div className="text-center px-8">
          <h3 className="text-xl md:text-2xl font-black uppercase tracking-tight theme-primary-text italic leading-tight">
            Asistente de creación automática
          </h3>
          <p className="text-xs md:text-sm font-bold text-white uppercase tracking-[0.3em] mt-2 mb-6">
            Paso {step} de 4
          </p>
        </div>
        <button onClick={onCancel} className="absolute right-0 top-0 opacity-40 hover:opacity-100 bg-white/5 hover:bg-white/10 p-2 rounded-full transition-all group shrink-0">
          <X size={20} className="group-hover:rotate-90 transition-transform" />
        </button>
      </div>

      {/* Step 1: Type Selection */}
      {step === 1 && (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4">
          <div className="text-center space-y-2 mb-8">
            <h4 className="text-xl font-black uppercase">¿Qué tipo de formato tendrá?</h4>
            <p className="text-sm opacity-60 font-medium">Elige la estructura base de tu competición</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={() => { setCompType('liga'); setStep(2); }}
              className={`group p-6 rounded-2xl border transition-all flex flex-col items-center gap-4 ${compType === 'liga' ? 'theme-primary-border bg-theme-primary/5' : 'border-white/5 bg-black/20 hover:border-white/10'}`}
            >
              <div className="w-14 h-14 rounded-2xl bg-black/40 flex items-center justify-center group-hover:scale-110 transition-transform">
                <LayoutGrid className="w-7 h-7 theme-primary-text" />
              </div>
              <div className="text-center">
                <span className="block font-black text-lg uppercase">Liga / Zonas</span>
                <span className="text-[10px] opacity-40 font-bold uppercase tracking-widest">Grupos + Playoffs</span>
              </div>
            </button>

            <button
              onClick={() => { setCompType('torneo_eliminacion'); setStep(2); }}
              className={`group p-6 rounded-2xl border transition-all flex flex-col items-center gap-4 ${compType === 'torneo_eliminacion' ? 'theme-primary-border bg-theme-primary/5' : 'border-white/5 bg-black/20 hover:border-white/10'}`}
            >
              <div className="w-14 h-14 rounded-2xl bg-black/40 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Trophy className="w-7 h-7 theme-primary-text" />
              </div>
              <div className="text-center">
                <span className="block font-black text-lg uppercase">Torneo Directo</span>
                <span className="text-[10px] opacity-40 font-bold uppercase tracking-widest">Eliminación Simple</span>
              </div>
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Name & Teams Selection */}
      {step === 2 && (
        <div className="space-y-8 animate-in fade-in slide-in-from-right-4">
          <div className="space-y-4">
            <label className="text-xs font-black uppercase tracking-widest opacity-50 block text-center">Nombre de la competición</label>
            <input
              type="text"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej: Copa Master Padel 2026"
              className="w-full bg-black/40 border-2 border-white/5 rounded-2xl p-5 font-black outline-none focus:theme-primary-border text-2xl placeholder:opacity-20"
            />
          </div>

          {userRole === 'superadmin' && (
            <div className="space-y-3 bg-amber-500/10 border border-amber-500/30 rounded-2xl p-4">
              <label className="text-xs font-black uppercase tracking-widest text-amber-500 flex items-center justify-center gap-2">
                <Trophy size={14} /> Categoría Master
              </label>
              <div className="flex items-center justify-between mt-2">
                <span className="text-[10px] text-white/70 leading-tight pr-4">
                  Al activar esta opción, los puntos de este torneo se sumarán directamente al <strong className="text-amber-500">Ranking General (Global)</strong>.
                </span>
                <button
                  onClick={() => setIsMaster(!isMaster)}
                  className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${isMaster ? 'bg-amber-500' : 'bg-white/20'}`}
                >
                  <div className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-white transition-transform ${isMaster ? 'translate-x-7' : 'translate-x-1'}`} />
                </button>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <label className="text-xs font-black uppercase tracking-widest theme-primary-text flex items-center justify-center gap-2">
              <Trophy size={14} /> Categoría de la Competición
            </label>
            <div className="flex flex-wrap gap-2 p-1">
              {['1era', '2da', '3ra', '4ta', '5ta', '6ta', '7ma'].map((cat) => (
                <button
                  key={cat}
                  onClick={() => setCategory(cat)}
                  className={`flex-1 py-4 rounded-xl font-black uppercase tracking-widest border transition-all ${category === cat ? 'theme-primary-bg text-black border-transparent shadow-lg scale-105' : 'border-white/5 bg-black/20 opacity-40 hover:opacity-100'}`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <label className="text-xs font-black uppercase tracking-widest theme-primary-text flex items-center justify-center gap-2">
              <Users size={14} /> Modalidad
            </label>
            <div className="flex gap-2 p-1">
              {[
                { id: 'Sin Restricción', label: 'Sin Restricción', extra: 'Género Libre' },
                { id: 'Masculino', label: 'Caballeros' },
                { id: 'Femenino', label: 'Damas' },
                { id: 'Mixto', label: 'Mixto' }
              ].map((g) => (
                <button
                  key={g.id}
                  onClick={() => setGender(g.id as any)}
                  className={`flex-1 py-4 rounded-xl font-black uppercase tracking-widest border transition-all flex flex-col items-center justify-center gap-0.5 ${gender === g.id ? 'theme-primary-bg text-black border-transparent shadow-lg scale-105' : 'border-white/5 bg-black/20 opacity-40 hover:opacity-100'}`}
                >
                  <span className="leading-none">{g.label}</span>
                  {g.extra && <span className="text-[7px] opacity-60 normal-case font-bold leading-none">{g.extra}</span>}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-10">
            <div className="flex justify-between items-end">
              <div className="space-y-1">
                <label className="text-xs font-black uppercase tracking-widest opacity-50 block text-center">Equipos Participantes</label>
                <div className="text-3xl font-black theme-primary-text">{selectedTeams.length} <span className="text-sm opacity-40">de {allTeams.length}</span></div>
              </div>
              <button
                onClick={selectAll}
                className="text-[10px] font-black uppercase tracking-widest bg-white/5 hover:bg-white/10 px-4 py-2 rounded-xl transition-all border border-white/5"
              >
                {selectedTeams.length === allTeams.length ? 'Quitar todos' : 'Seleccionar todos'}
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 max-h-72 overflow-y-auto pr-3 custom-scrollbar p-1">
              {allTeams.map((t) => (
                <div
                  key={t.id}
                  onClick={() => toggleTeam(t.id)}
                  className={`p-4 rounded-2xl border-2 cursor-pointer flex items-center gap-4 transition-all ${selectedTeams.includes(t.id) ? 'theme-primary-border bg-white/5' : 'border-white/5 hover:border-white/10 bg-black/20'}`}
                >
                  <div className={`w-6 h-6 rounded-lg flex items-center justify-center border-2 transition-all ${selectedTeams.includes(t.id) ? 'theme-primary-bg border-transparent' : 'border-white/10'}`}>
                    {selectedTeams.includes(t.id) && <CheckCircle2 size={14} className="text-black" />}
                  </div>
                  <span className="text-sm font-black uppercase truncate">{t.name}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-between pt-6 border-t border-white/5">
            <button onClick={() => setStep(1)} className="px-6 py-3 rounded-xl font-bold uppercase tracking-widest opacity-40 hover:opacity-100 transition-all text-xs">Atrás</button>
            <button
              disabled={!name || selectedTeams.length < 2}
              onClick={() => setStep(3)}
              className="theme-primary-bg text-black px-10 py-3.5 rounded-xl font-black uppercase tracking-widest disabled:opacity-30 transition-all shadow-xl hover:scale-[1.02] active:scale-95 text-sm"
            >
              Continuar
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Format Configuration */}
      {step === 3 && (
        <div className="space-y-8 animate-in fade-in slide-in-from-right-4">
          {compType === 'liga' ? (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <label className="text-xs font-black uppercase tracking-widest opacity-50 block text-center">Division en Zonas</label>
                  <select
                    value={numZones}
                    onChange={(e) => setNumZones(Number(e.target.value))}
                    className="w-full bg-black/40 border-2 border-white/5 rounded-2xl p-4 font-black outline-none focus:theme-primary-border appearance-none"
                  >
                    {[1, 2, 3, 4, 6, 8, 12, 16].map(n => (
                      <option key={n} value={n} className="bg-slate-900">{n} {n === 1 ? 'Zona Única' : 'Zonas'}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-3">
                  <label className="text-xs font-black uppercase tracking-widest opacity-50 block text-center">Sistema de partidos</label>
                  <select
                    value={doubleRound ? 'ida_vuelta' : 'ida'}
                    onChange={(e) => setDoubleRound(e.target.value === 'ida_vuelta')}
                    className="w-full bg-black/40 border-2 border-white/5 rounded-2xl p-4 font-black outline-none focus:theme-primary-border appearance-none"
                  >
                    <option value="ida" className="bg-slate-900">Solo Ida (Rápido)</option>
                    <option value="ida_vuelta" className="bg-slate-900">Ida y Vuelta (Extenso)</option>
                  </select>
                </div>
              </div>

              <div className="bg-black/40 p-6 rounded-[var(--radius-large)] border-2 border-white/5 space-y-6">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <span className="block font-black uppercase text-lg">¿Hay clasificación / Playoffs?</span>
                    <span className="text-xs opacity-50 font-bold uppercase">Define si habrá una fase eliminatoria posterior</span>
                  </div>
                  <button
                    onClick={() => setHasPlayoffs(!hasPlayoffs)}
                    className={`w-14 h-8 rounded-full transition-all relative ${hasPlayoffs ? 'theme-primary-bg' : 'bg-white/10'}`}
                  >
                    <div className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow-lg transition-all ${hasPlayoffs ? 'right-1' : 'left-1'}`} />
                  </button>
                </div>

                {hasPlayoffs && (
                  <div className="space-y-3 animate-in zoom-in-95 duration-300">
                    <label className="text-xs font-black uppercase tracking-widest opacity-50 block text-center">Clasificados por cada zona</label>
                    <div className="flex items-center gap-4">
                      {[1, 2, 3, 4].map(n => (
                        <button
                          key={n}
                          onClick={() => setQualifiersPerZone(n)}
                          className={`flex-1 py-3 rounded-xl font-black border-2 transition-all ${qualifiersPerZone === n ? 'theme-primary-border theme-primary-text bg-white/5' : 'border-white/5 hover:border-white/10'}`}
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                    <p className="text-[10px] theme-primary-text font-black uppercase tracking-wider mt-2 flex items-center gap-2">
                      <Shield size={12} /> Se destacarán los primeros {qualifiersPerZone} de cada tabla
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="bg-amber-500/10 border-2 border-amber-500/20 p-6 rounded-[var(--radius-large)] space-y-4">
                <div className="flex items-center gap-3 text-amber-400">
                  <AlertCircle className="shrink-0" />
                  <span className="font-black uppercase tracking-widest">Aviso de Formato Eliminatorio</span>
                </div>
                <div className="text-sm font-medium opacity-80 leading-relaxed">
                  Has seleccionado <strong>{selectedTeams.length} equipos</strong>.
                  {selectedTeams.length % 2 !== 0 ? (
                    <p className="mt-2">
                      Como el número es <strong>impar</strong>, algunos equipos pasarán a la siguiente ronda directamente (PASE DIRECTO) mediante sorteo automático para equilibrar la llave.
                    </p>
                  ) : (
                    <p className="mt-2">
                      El sistema armará llaves simétricas. Se requiere que los resultados se carguen en orden para avanzar de ronda.
                    </p>
                  )}
                </div>
              </div>

              <div className="bg-black/40 p-6 rounded-[var(--radius-large)] border-2 border-white/5 space-y-1">
                <span className="text-xs font-black uppercase opacity-50 tracking-widest">Ronda Inicial Estimada</span>
                <p className="text-2xl font-black uppercase theme-accent-text">
                  {selectedTeams.length <= 2 ? 'Final Directa' :
                    selectedTeams.length <= 4 ? 'Semifinales' :
                      selectedTeams.length <= 8 ? 'Cuartos de final' :
                        selectedTeams.length <= 16 ? 'Octavos de final' : 'Ronda de 32'}
                </p>
              </div>
            </div>
          )}

          <div className="flex justify-between pt-6 border-t border-white/5">
            <button onClick={() => setStep(2)} className="px-6 py-3 rounded-xl font-bold uppercase tracking-widest opacity-40 hover:opacity-100 transition-all text-xs">Atrás</button>
            <button
              onClick={() => setStep(4)}
              className="theme-primary-bg text-black px-10 py-3.5 rounded-xl font-black uppercase tracking-widest transition-all shadow-xl hover:scale-[1.02] active:scale-95 text-sm"
            >
              Confirmar
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Final Summary & Generate */}
      {step === 4 && (
        <div className="space-y-8 animate-in fade-in slide-in-from-right-4">
          <div className="text-center space-y-2 mb-4">
            <Trophy className="w-16 h-16 theme-primary-text mx-auto mb-4" />
            <h4 className="text-2xl font-black uppercase">¡Todo listo para brillar!</h4>
            <p className="text-sm opacity-60">Revisa que la configuración sea correcta antes de lanzar</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-black/30 p-5 rounded-2xl border border-white/5">
              <span className="text-[10px] font-black uppercase opacity-40 block mb-1">Competición</span>
              <span className="font-black text-lg truncate block">{name}</span>
            </div>
            <div className="bg-black/30 p-5 rounded-2xl border border-white/5">
              <span className="text-[10px] font-black uppercase opacity-40 block mb-1">Participantes</span>
              <span className="font-black text-lg block">{selectedTeams.length} Equipos</span>
            </div>
            <div className="bg-black/30 p-5 rounded-2xl border border-white/5">
              <span className="text-[10px] font-black uppercase opacity-40 block mb-1">Tipo de Formato</span>
              <span className="font-black text-lg block uppercase">{compType === 'liga' ? 'Liga / Zonas' : 'Eliminación Directa'}</span>
            </div>
            <div className="bg-black/30 p-5 rounded-2xl border border-white/5">
              <span className="text-[10px] font-black uppercase opacity-40 block mb-1">Configuración</span>
              <span className="font-black text-lg block">
                {compType === 'liga'
                  ? `${numZones} Zonas ${hasPlayoffs ? `(Clas. ${qualifiersPerZone})` : '(Liga Pura)'}`
                  : 'Llaves Automáticas'}
              </span>
            </div>
          </div>

          <div className="flex justify-between pt-6 border-t border-white/5">
            <button onClick={() => setStep(3)} className="px-6 py-3 rounded-xl font-bold uppercase tracking-widest opacity-40 hover:opacity-100 transition-all text-xs">Editar</button>
            <button
              onClick={handleGenerate}
              className="theme-accent-bg text-black px-12 py-3.5 rounded-xl font-black uppercase tracking-widest transition-all shadow-[0_10px_30px_rgba(251,191,36,0.2)] hover:scale-[1.02] active:scale-95 text-sm italic"
            >
              ¡Lanzar Competición!
            </button>
          </div>
        </div>
      )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
